import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

export const stepTemplatesApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "stepTemplatesApi",
    baseUrl: "/api/step-templates",
  }),
  reducerPath: "stepTemplatesApi",
  endpoints: (builder) => ({
    createEventTemplate: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `/${id}/event-templates`,
        method: "POST",
        body: data,
      }),
    }),
    createDocumentTemplate: builder.mutation<
      any,
      { stepTemplateId: string; docTemplateId: any }
    >({
      query: ({ stepTemplateId, docTemplateId }) => ({
        url: `/${stepTemplateId}/document-templates/${docTemplateId}`,
        method: "POST",
        body: { stepTemplateId, docTemplateId },
      }),
    }),
    deleteStepTemplate: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
    }),
    /**
     * PUT /api/step-templates/:id
     *
     * Partial update — only the fields supplied in `data` are touched. Send
     * `requiredFirstClassItem: null` to clear an already-set first-class item;
     * omitting the key leaves the existing value alone.
     */
    updateStepTemplate: builder.mutation<
      any,
      {
        id: string;
        data: {
          name?: string;
          order?: number;
          category?: string;
          active?: boolean;
          requiredFirstClassItem?: "CLIENT_NEED" | "LISTING_SHEET" | null;
        };
      }
    >({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: "PUT",
        body: data,
      }),
    }),
    deleteStepTemplateDocument: builder.mutation<
      any,
      { stepTemplateId: string; docTemplateId: string }
    >({
      query: ({ stepTemplateId, docTemplateId }) => ({
        url: `/${stepTemplateId}/document-templates/${docTemplateId}`,
        method: "DELETE",
      }),
    }),
    deleteStepTemplateEvent: builder.mutation<
      any,
      { stepTemplateId: string; eventTemplateId: string }
    >({
      query: ({ stepTemplateId, eventTemplateId }) => ({
        url: `/${stepTemplateId}/event-templates/${eventTemplateId}`,
        method: "DELETE",
      }),
    }),
    /**
     * PATCH /api/step-templates/:stepTemplateId/document-templates/:docTemplateId/suggested
     * Body: { suggested: boolean }
     * Toggles the suggested flag on the join row. At workflow-merge time
     * this propagates to step_document.suggested.
     */
    setStepDocTemplateSuggested: builder.mutation<
      any,
      { stepTemplateId: string; docTemplateId: string; suggested: boolean }
    >({
      query: ({ stepTemplateId, docTemplateId, suggested }) => ({
        url: `/${stepTemplateId}/document-templates/${docTemplateId}/suggested`,
        method: "PATCH",
        body: { suggested },
      }),
    }),
    /** Symmetric event variant. */
    setStepEventTemplateSuggested: builder.mutation<
      any,
      { stepTemplateId: string; eventTemplateId: string; suggested: boolean }
    >({
      query: ({ stepTemplateId, eventTemplateId, suggested }) => ({
        url: `/${stepTemplateId}/event-templates/${eventTemplateId}/suggested`,
        method: "PATCH",
        body: { suggested },
      }),
    }),
  }),
});

export const {
  useCreateEventTemplateMutation,
  useCreateDocumentTemplateMutation,
  useDeleteStepTemplateMutation,
  useDeleteStepTemplateDocumentMutation,
  useDeleteStepTemplateEventMutation,
  useUpdateStepTemplateMutation,
  useSetStepDocTemplateSuggestedMutation,
  useSetStepEventTemplateSuggestedMutation,
} = stepTemplatesApi;
